import { LightningBoltIcon } from '@radix-ui/react-icons';
import React from 'react';

import { type CmsSecReviewFinding, CmsSecReviewFindingSeverity, type CmsSecReviewView } from '@ndvr/cms-api';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils/cn';

const severityClassName = (severity: CmsSecReviewFindingSeverity): string => {
    switch (severity) {
        case CmsSecReviewFindingSeverity.HIGH:
            return 'border-red-200 bg-red-50 text-red-700';
        case CmsSecReviewFindingSeverity.MEDIUM:
            return 'border-gold-100 bg-gold-0 text-gold-700';
        case CmsSecReviewFindingSeverity.LOW:
            return 'border-seafoam-200 bg-seafoam-0 text-seafoam-700';
        case CmsSecReviewFindingSeverity.INFO:
            return 'border-espresso-100 bg-espresso-50 text-espresso-600';
    }
};

const formatConfidence = (confidence: number): string => `${Math.round(confidence * 100)}% confidence`;

const severityOrder = [
    CmsSecReviewFindingSeverity.HIGH,
    CmsSecReviewFindingSeverity.MEDIUM,
    CmsSecReviewFindingSeverity.LOW,
    CmsSecReviewFindingSeverity.INFO,
];

const reviewableLeafFields = new Set([
    'answer',
    'attributionName',
    'attributionTitle',
    'bio',
    'body',
    'ctaLabel',
    'description',
    'eyebrow',
    'heading',
    'headline',
    'label',
    'linkLabel',
    'name',
    'question',
    'quote',
    'submitLabel',
    'successMessage',
    'title',
]);

interface FindingGroup {
    key: string;
    anchorPath?: string;
    title: string;
    findings: CmsSecReviewFinding[];
    snippets: string[];
    severityCounts: Partial<Record<CmsSecReviewFindingSeverity, number>>;
    highestSeverity: CmsSecReviewFindingSeverity;
}

const normalizeContentPath = (contentPath?: string): string | undefined => {
    const trimmedPath = contentPath?.trim();
    if (!trimmedPath) {
        return undefined;
    }

    const pathParts = trimmedPath.split('.');
    const lastPart = pathParts[pathParts.length - 1];
    if (pathParts.length > 1 && reviewableLeafFields.has(lastPart)) {
        return pathParts.slice(0, -1).join('.');
    }

    return trimmedPath;
};

const humanizePathSegment = (segment: string): string => {
    const arrayMatch = segment.match(/^([A-Za-z]+)\[(\d+)]$/);
    if (arrayMatch) {
        const [, collectionName, index] = arrayMatch;
        const displayIndex = Number(index) + 1;
        if (collectionName === 'modules') {
            return `Module ${displayIndex}`;
        }
        if (collectionName === 'items') {
            return `Item ${displayIndex}`;
        }
        if (collectionName === 'steps') {
            return `Step ${displayIndex}`;
        }
        return `${humanizePathSegment(collectionName)} ${displayIndex}`;
    }

    const knownLabels: Record<string, string> = {
        advisorDisplayName: 'Advisor display name',
        advisorProfile: 'Advisor profile',
        contactSection: 'Contact section',
        cta: 'CTA',
        faq: 'FAQ',
        hero: 'Hero',
        importantDisclosures: 'Important disclosures',
        infoCardGrid: 'Info card grid',
        leadForm: 'Lead form',
        mediaTextSection: 'Media text section',
        metaDescription: 'Meta description',
        pageTitle: 'Page title',
        processSteps: 'Process steps',
        quoteSection: 'Quote section',
        resourceList: 'Resource list',
        siteName: 'Site name',
    };

    if (knownLabels[segment]) {
        return knownLabels[segment];
    }

    const spaced = segment.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const formatContentPathLabel = (contentPath?: string): string => {
    if (!contentPath) {
        return 'Unmapped content';
    }

    return contentPath.split('.').map(humanizePathSegment).join(' · ');
};

const uniqueNonBlank = (values: Array<string | undefined>): string[] =>
    Array.from(new Set(values.map((value) => value?.trim()).filter((value): value is string => !!value)));

const highestSeverityFor = (
    severityCounts: Partial<Record<CmsSecReviewFindingSeverity, number>>,
): CmsSecReviewFindingSeverity =>
    severityOrder.find((severity) => (severityCounts[severity] ?? 0) > 0) ?? CmsSecReviewFindingSeverity.INFO;

const formatFindingCount = (count: number): string => `${count} finding${count === 1 ? '' : 's'}`;

const buildFindingGroups = (findings: CmsSecReviewFinding[]): FindingGroup[] => {
    const groups = new Map<string, FindingGroup>();

    findings.forEach((finding, index) => {
        const anchorPath = normalizeContentPath(finding.contentPath);
        const key = anchorPath ?? `unmapped-${index}`;
        const group =
            groups.get(key) ??
            ({
                key,
                anchorPath,
                title: formatContentPathLabel(anchorPath),
                findings: [],
                snippets: [],
                severityCounts: {},
                highestSeverity: CmsSecReviewFindingSeverity.INFO,
            } satisfies FindingGroup);

        group.findings.push(finding);
        group.snippets = uniqueNonBlank([...group.snippets, finding.snippet]);
        group.severityCounts[finding.severity] = (group.severityCounts[finding.severity] ?? 0) + 1;
        group.highestSeverity = highestSeverityFor(group.severityCounts);
        groups.set(key, group);
    });

    return Array.from(groups.values());
};

interface CmsSecAiReviewPanelProps {
    review?: CmsSecReviewView | null;
    isRequestPending?: boolean;
    requestDisabledReason?: string;
    onRequestReview?: () => void;
    showRequestAction?: boolean;
}

export const CmsSecAiReviewPanel = ({
    review,
    isRequestPending = false,
    requestDisabledReason,
    onRequestReview,
    showRequestAction = true,
}: CmsSecAiReviewPanelProps) => {
    const findings = review?.findings ?? [];
    const findingGroups = buildFindingGroups(findings);
    const canRequest = !!onRequestReview && !requestDisabledReason;

    return (
        <section className="border-espresso-100 rounded-md border bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2">
                        <LightningBoltIcon className="text-gold-500 h-4 w-4" />
                        <h2 className="font-semibold">AI-assisted SEC pre-review</h2>
                    </div>
                    <p className="text-espresso-500 mt-1 text-sm">
                        Automated findings are a drafting aid only. Human review and approval remain required.
                    </p>
                    {review && (
                        <p className="text-espresso-400 mt-1 text-xs">
                            Rubric {review.rubricVersion} · {review.modelMetadata.model}
                        </p>
                    )}
                </div>
                {showRequestAction && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={onRequestReview}
                        disabled={!canRequest || isRequestPending}
                        title={requestDisabledReason}
                    >
                        <LightningBoltIcon className={cn('h-4 w-4', isRequestPending && 'animate-pulse')} />
                        {isRequestPending ? 'Requesting...' : 'Request AI review'}
                    </Button>
                )}
            </div>

            {!review ? (
                <p className="text-espresso-400 mt-4 text-sm">
                    No AI SEC pre-review has been generated for this immutable version yet.
                </p>
            ) : findings.length === 0 ? (
                <p className="text-seafoam-700 bg-seafoam-0 border-seafoam-200 mt-4 rounded-md border px-3 py-2 text-sm">
                    AI pre-review returned no guideline findings. This is not an approval.
                </p>
            ) : (
                <div className="mt-4 space-y-4">
                    {findingGroups.map((group) => (
                        <article key={group.key} className="border-espresso-100 overflow-hidden rounded-md border">
                            <div className="border-espresso-100 bg-espresso-50 border-b px-3 py-3">
                                <div className="flex flex-wrap items-start gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Badge
                                                variant="outline"
                                                className={cn('text-[10px]', severityClassName(group.highestSeverity))}
                                            >
                                                {group.highestSeverity}
                                            </Badge>
                                            <h3 className="text-espresso-700 text-sm font-semibold">{group.title}</h3>
                                            <span className="text-espresso-400 text-xs">
                                                {formatFindingCount(group.findings.length)}
                                            </span>
                                        </div>
                                        {group.anchorPath && (
                                            <p className="text-espresso-500 mt-1 font-mono text-xs break-all">
                                                {group.anchorPath}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap justify-end gap-1">
                                        {severityOrder.map((severity) => {
                                            const count = group.severityCounts[severity] ?? 0;
                                            if (count === 0) {
                                                return null;
                                            }

                                            return (
                                                <Badge
                                                    key={severity}
                                                    variant="outline"
                                                    className={cn('text-[10px]', severityClassName(severity))}
                                                >
                                                    {count} {severity}
                                                </Badge>
                                            );
                                        })}
                                    </div>
                                </div>
                                {group.snippets.length > 0 && (
                                    <div className="mt-3 grid gap-2">
                                        {group.snippets.map((snippet) => (
                                            <p
                                                key={snippet}
                                                className="text-espresso-700 rounded bg-white px-2 py-1 text-sm"
                                            >
                                                {snippet}
                                            </p>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="divide-espresso-100 divide-y">
                                {group.findings.map((finding, index) => {
                                    const exactPath = finding.contentPath?.trim();
                                    const showExactPath = exactPath && exactPath !== group.anchorPath;

                                    return (
                                        <div
                                            key={`${finding.contentPath ?? 'finding'}-${finding.ruleId}-${index}`}
                                            className="p-3"
                                        >
                                            <div className="flex flex-wrap items-center gap-2">
                                                <Badge
                                                    variant="outline"
                                                    className={cn('text-[10px]', severityClassName(finding.severity))}
                                                >
                                                    {finding.severity}
                                                </Badge>
                                                <span className="text-espresso-700 text-sm font-semibold">
                                                    {finding.ruleId}
                                                </span>
                                                <span className="text-espresso-300">/</span>
                                                <span className="text-espresso-500 text-sm">{finding.category}</span>
                                                <span className="text-espresso-400 ml-auto text-xs">
                                                    {formatConfidence(finding.confidence)}
                                                </span>
                                            </div>
                                            {showExactPath && (
                                                <p className="text-espresso-400 mt-1 font-mono text-xs break-all">
                                                    Exact path: {exactPath}
                                                </p>
                                            )}
                                            <dl className="mt-2 grid gap-2 text-sm">
                                                <div>
                                                    <dt className="text-espresso-400 text-xs font-medium tracking-wide uppercase">
                                                        Rationale
                                                    </dt>
                                                    <dd className="text-espresso-700">{finding.rationale}</dd>
                                                </div>
                                                <div>
                                                    <dt className="text-espresso-400 text-xs font-medium tracking-wide uppercase">
                                                        Suggested action
                                                    </dt>
                                                    <dd className="text-espresso-700">
                                                        {finding.suggestedReviewerAction}
                                                    </dd>
                                                </div>
                                            </dl>
                                        </div>
                                    );
                                })}
                            </div>
                        </article>
                    ))}
                </div>
            )}
        </section>
    );
};
